import { useAxios } from 'hooks/useAxios';
import { ACLCheck, AxiosErrorHandler } from 'shared/Helpers';
import { GanttChartChange } from 'types/global';
import { AxiosRequestConfig } from 'axios';
import { BASE_URL, BASE_URL_V2 } from 'shared/constants';
import { GanttChartMode } from 'sb/shared/constants';

export const useProjectApi = () => {
  const { axios } = useAxios(BASE_URL);

  /**
   * Fetch project
   * @param project_id
   */
  const fetchProject = async (project_id: number) => {
    try {
      const res = await axios.get(`/api/project/${project_id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update Project Tasks
   * @param project_id
   * @param method
   * @param change
   */
  const upsertProjectTask = async (project_id: number, method: 'post' | 'patch', change: GanttChartChange) => {
    try {
      const config: AxiosRequestConfig = {
        url: `/api/project/${project_id}/task`,
        method: method,
        data: change
      };
      const res = await axios(config);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Delete Project Task
   * @param project_id
   * @param task_id
   */
  const deleteProjectTask = async (project_id: number, task_id: number) => {
    try {
      const res = await axios.delete(`/api/project/${project_id}/task/${task_id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update Project Dependency
   * @param project_id
   * @param method
   * @param change
   */
  const upsertProjectDependency = async (project_id: number, method: 'post' | 'patch', change: GanttChartChange) => {
    try {
      const config: AxiosRequestConfig = {
        url: `/api/project/${project_id}/dependency`,
        method: method,
        data: change
      };
      const res = await axios(config);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Delete Project Dependency
   * @param project_id
   * @param id
   */
  const deleteProjectDependency = async (project_id: number, id: number) => {
    try {
      const res = await axios.delete(`/api/project/${project_id}/dependency/${id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update Project Assignment
   * @param project_id
   * @param method
   * @param change
   */
  const upsertProjectAssignment = async (project_id: number, method: 'post' | 'patch', change: GanttChartChange) => {
    try {
      const config: AxiosRequestConfig = {
        url: `/api/project/${project_id}/assignment`,
        method: method,
        data: change
      };
      const res = await axios(config);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Delete Project Assignment
   * @param project_id
   * @param id
   */
  const deleteProjectAssignment = async (project_id: number, id: number) => {
    try {
      const res = await axios.delete(`/api/project/${project_id}/assignment/${id}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetches a list of renewals based on the given status and profile ID (optional).
   * @param {string} status - The status of the renewals. Can be 'active' or 'expired'.
   * @param {number} [profileId] - The profile ID to filter the renewals by (optional).
   * @param {Date} [expiresIn] - An optional renewal date cutoff
   * @returns {Promise<Array<object>>} - A promise that resolves to an array of renewal data objects.
   * @throws {Error} - If an error occurs during the request or if the response status is not 'success'.
   */
  const fetchRenewals = async (status: 'active' | 'expired', profileId?: number, expiresIn?: Date): Promise<any> => {
    try {
      // Early-access users get the new deal-table calendar (incl. proposals);
      // everyone else falls back to the legacy smart-calendar flow.
      const endpoint = ACLCheck(['read:early_access']) ? `/deals/calendar` : `/smart-calendar/renewals`;
      const res = await axios.get(endpoint, {
        params: { status, profileId, expires_in: expiresIn },
        baseURL: BASE_URL_V2
      });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res.data?.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetches portfolio renewal data based on the specified status and optional profile ID.
   * @param {('active' | 'expired')} status - The status of the portfolio renewals to fetch. Can be either 'active' or 'expired'.
   * @param {number} [profileId] - An optional profile ID to filter the renewals.
   * @param {Date} [expiresIn] - An optional renewal date cutoff
   * @returns {Promise<any | undefined>} A promise that resolves to the fetched renewal data, or undefined if an error occurs.
   */
  const fetchPortfolioRenewals = async (
    status: 'active' | 'expired',
    profileId?: number,
    expiresIn?: Date
  ): Promise<any | undefined> => {
    try {
      // Early-access users get the new deal-table calendar; legacy fallback otherwise.
      const endpoint = ACLCheck(['read:early_access'])
        ? `/deals/calendar/portfolio`
        : `/smart-calendar/renewals/portfolio`;
      const res = await axios.get(endpoint, {
        params: { status, profileId, expires_in: expiresIn },
        baseURL: BASE_URL_V2
      });
      return res.data;
    } catch (e) {
      await AxiosErrorHandler(e);
    }
  };

  const fetchRenewalsByMode = (
    mode: GanttChartMode,
    status: 'expired' | 'active',
    profileId: number | undefined,
    expiresIn: Date | undefined
  ) => {
    if (mode == GanttChartMode.OWN_CALENDER) {
      return fetchRenewals(status, profileId, expiresIn);
    } else {
      return fetchPortfolioRenewals(status, profileId, expiresIn);
    }
  };

  /**
   * Fetch supplier renewals
   * @param id
   */
  const fetchSupplierRenewals = async (id: number) => {
    try {
      const res = await axios.get(`/secure/supplier/${id}/renewals`);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
      return Promise.reject(Error(res.data?.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch supplier renewals
   * @param id
   * @param renewalTimeframe
   */
  const fetchNPIRenewals = async (id: string, renewalTimeframe?: string) => {
    try {
      const res = await axios.get(`/secure/renewals/${id}`, {
        params: { renewalTimeframe: renewalTimeframe }
      });
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
      return Promise.reject(Error(res.data?.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  return {
    fetchProject,
    upsertProjectTask,
    deleteProjectTask,
    upsertProjectDependency,
    deleteProjectDependency,
    upsertProjectAssignment,
    deleteProjectAssignment,
    fetchRenewals,
    fetchPortfolioRenewals,
    fetchRenewalsByMode,
    fetchSupplierRenewals,
    fetchNPIRenewals
  };
};
